import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { CapabilityCatalogue, Role, RoleChange, RoleRecord } from '@/types/domain'

/* Roles and what each one may do — migration 0021.
 *
 * These endpoints live under /admin behind `admin.roles.manage`, which is a
 * NARROWER guard than the rest of user management: whoever holds it can grant
 * themselves every other capability in the product, so a founder delegating
 * day-to-day account upkeep should not be handing this over with it.
 *
 * There is no mock branch in this file, deliberately, where the other settings
 * hooks have one. A mocked role list would be a mocked ACCESS MATRIX — the
 * screen would show permissions that nothing enforces, and the one thing worse
 * than an authorization screen that refuses to load is one that lies about what
 * is granted. With mocks on, these queries fail and the screen says so.
 */

const roleKeys = {
  all: ['settings', 'roles'] as const,
  capabilities: ['settings', 'capabilities'] as const,
}

export function useRoles() {
  return useQuery<RoleRecord[]>({
    queryKey: roleKeys.all,
    queryFn: async () => (await api.get<{ items: RoleRecord[] }>('/admin/roles')).items,
  })
}

/** The vocabulary the role editor renders: every capability the SERVER
 *  registers, plus the surfaces and levels the scope editor offers.
 *
 *  Fetched rather than hard-coded because the server's registry is what
 *  actually gets checked — a capability this client invented would be a
 *  permission that does nothing, rendered as though it did something.
 *
 *  Effectively immutable for the life of a deployment, so it is cached hard:
 *  it changes when the server is redeployed, and a refetch on window focus
 *  would be a request per tab switch for a list that cannot have moved. */
export function useCapabilityCatalogue() {
  return useQuery<CapabilityCatalogue>({
    queryKey: roleKeys.capabilities,
    queryFn: async () => await api.get<CapabilityCatalogue>('/admin/capabilities'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export function useCreateRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (change: RoleChange) => await api.post<RoleRecord>('/admin/roles', change),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleKeys.all })
    },
  })
}

export function useAmendRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ key, change }: { key: Role; change: RoleChange }) =>
      await api.put<RoleRecord>(`/admin/roles/${encodeURIComponent(key)}`, change),
    onSuccess: () => {
      // Both lists: amending a role changes what its holders can do, so the
      // user list's effective-capability column is stale too.
      void qc.invalidateQueries({ queryKey: roleKeys.all })
      void qc.invalidateQueries({ queryKey: ['settings', 'users'] })
      // And the CALLER's own session: a founder who just edited their own
      // role's capabilities is looking at a navigation rendered from the old
      // set. Re-reading /session is what makes the change visible without a
      // reload — the server has already applied it.
      void qc.invalidateQueries({ queryKey: ['session'] })
    },
  })
}

export function useDeleteRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (key: Role) =>
      await api.delete<void>(`/admin/roles/${encodeURIComponent(key)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleKeys.all })
    },
  })
}
